import React, { useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ClockOutline, CloseOutline, MoreVerticalFill } from '@lyearn/icons';
import { Button, IconButton, Popover, Stack, Typography } from '@lyearn/ui';
import { RecommendedActionStatusEnum } from '@/types/schema';

import { InsightsRecommendationItem, UpdateRecommendationStatusProps } from '../types';

// TODO: @KanishkaRajputd handle popover like Move To

const SNOOZE_OPTIONS = [
  { label: '24Hours', days: 1 },
  { label: '3Days', days: 3 },
  { label: '7Days', days: 7 },
  { label: '15Days', days: 15 },
];

type Props = {
  recommendation: InsightsRecommendationItem;
  updateRecommendationStatus: (props: UpdateRecommendationStatusProps) => void;
};

const StatusActionMenuContent = (props: Props & { onClose: () => void }) => {
  const { recommendation, updateRecommendationStatus, onClose } = props;
  const { t } = useTranslation(['Home']);
  const [uiState, setUiState] = useState<'main' | 'snooze'>('main');

  const onDismissClick = () => {
    updateRecommendationStatus({
      recommendationId: recommendation.id,
      status: RecommendedActionStatusEnum.Dismissed,
    });
    onClose();
    setUiState('main');
  };

  const onSnoozeClick = (days: number) => {
    updateRecommendationStatus({
      recommendationId: recommendation.id,
      status: RecommendedActionStatusEnum.Snoozed,
      snoozeUntil: new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
    });
    onClose();
    setUiState('main');
  };

  if (uiState === 'main') {
    return (
      <Popover.Content key="main" className="py-12 px-8 w-[214px]" renderAs="popover">
        <Stack direction="col" gapY={4}>
          <Button
            fullWidth
            appearance="secondary"
            className="px-3 flex justify-between"
            endIcon={<ClockOutline className="w-20 h-20" fillColorToken="icon-secondary" />}
            shape="square"
            variant="ghost"
            onClick={() => setUiState('snooze')}>
            <Typography color="text-text-primary" variant="body-short-02">
              {t('Home:AiRecommendationWidget.SnoozeFor')}...
            </Typography>
          </Button>
          <Button
            fullWidth
            appearance="secondary"
            className="px-0 flex justify-between"
            endIcon={<CloseOutline className="w-20 h-20" fillColorToken="icon-error" />}
            shape="square"
            variant="ghost"
            onClick={onDismissClick}>
            <Typography color="text-text-error" variant="body-short-02">
              {t('Home:AiRecommendationWidget.DismissAction')}
            </Typography>
          </Button>
        </Stack>
      </Popover.Content>
    );
  }

  if (uiState === 'snooze') {
    return (
      <Popover.Content key="snooze" className="py-12 px-8 w-[214px]" renderAs="popover">
        <Stack direction="col">
          <Typography className="px-20 pb-4" color="text-text-placeholder" variant="label-caps">
            {t('Home:AiRecommendationWidget.SnoozeFor')}
          </Typography>
          {SNOOZE_OPTIONS.map((option) => (
            <Button
              key={option.days}
              fullWidth
              appearance="secondary"
              className="px-12 justify-start hover:bg-bg-hover"
              shape="square"
              variant="ghost"
              onClick={() => onSnoozeClick(option.days)}>
              <Typography color="text-text-primary" variant="body-short-02">
                {t(`Home:AiRecommendationWidget.${option.label}`)}
              </Typography>
            </Button>
          ))}
        </Stack>
      </Popover.Content>
    );
  }
};

function StatusActionMenu(props: Props) {
  return (
    <Popover>
      {({ onClose }) => (
        <>
          <Popover.Button>
            <IconButton Icon={MoreVerticalFill} shape="square" size="l" />
          </Popover.Button>
          <Popover.Content>
            <StatusActionMenuContent onClose={onClose} {...props} />
          </Popover.Content>
        </>
      )}
    </Popover>
  );
}

export default React.memo(StatusActionMenu);
